
import React, { Component } from 'react';

import { ToggleButton, Box, Pagination, Tooltip, Typography, createTheme, Popover } from '@mui/material';
import { DataGrid, useGridApiContext, useGridState, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid';

import { generateRandomTag } from './../../helpers/utils';
import CustomFooter from './CustomFooter';
import MenuIcon from '@mui/icons-material/MoreVert';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const theme = createTheme({
    palette: {
        primary: {
            main: '#d01a62',
        },
    },
    typography: {
        fontFamily: '"Poppins", sans-serif',
        fontSize: 16
    }
})

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton sx={{textTransform: 'none', fontSize: 16}} />
            <GridToolbarFilterButton sx={{textTransform: 'none', fontSize: 16}} />
            {/* TODO: remove density selector on mobile */}
            <GridToolbarDensitySelector sx={{textTransform: 'none', fontSize: 16}} />
            {/* <GridToolbarExport /> */}
        </GridToolbarContainer>
    )
}

class MuiCustomGrid extends Component {

    state = {
        refreshing: undefined, timer: undefined, pausedAt: undefined, displayHelp: undefined,
        rows: undefined, columns: undefined,
        selectionModel: undefined, sortModel: undefined,
        columnMenuOpen: undefined, columnMenuRef: undefined, menuColumn: undefined
    }

    constructor(props) {
        super(props)

        this.state.refreshing = props.refreshing? props.refreshing : true
        this.state.timer = props.timer? props.timer : 60
        this.state.pausedAt = props.pausedAt? props.pausedAt : Date.now()
        this.state.displayHelp = props.displayHelp? props.displayHelp : false
        this.state.rows = props.rows? props.rows : []
        this.state.columns = props.columns? props.columns : []
        this.state.selectionModel = props.selectionModel? props.selectionModel : 60
        this.state.sortModel = props.sortModel? props.sortModel : 60
        this.state.columnMenuOpen = false

        this.setSelectionModel = this.setSelectionModel.bind(this)
        this.setSortingModel = this.setSortingModel.bind(this)
        this.sortByOrder = this.sortByOrder.bind(this)
        this.openColumnMenu = this.openColumnMenu.bind(this)
        this.closeColumnMenu = this.closeColumnMenu.bind(this)
        this.popoverOnClick = this.popoverOnClick.bind(this)
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        console.log(error)
    }

    componentDidUpdate(newProps) {
        if (newProps.dataUpdated) {
            this.setState({ rows: newProps.rows, columns: newProps.columns })
        }
    }

    getDataViewColumnLayout(columns) {
        for (let i = 0; i < columns.length; i++) {
            let column = columns[i]
            let element = this.createCustomHeader(column)
            column.renderHeader = () => ( element )
        }

        return columns
    }
    
    setSelectionModel(selectionModel) {
        this.setState({selectionModel})
    }

    setSortingModel(sortModel) {
        this.state({sortModel})
    }

    sortByOrder(field, sort) {
        this.setState({sortModel: [ { field, sort } ]})
    }

    openColumnMenu(event, column) {
        let element = event.currentTarget
        this.setState({columnMenuOpen: true, columnMenuRef: element, menuColumn: column})
    }

    closeColumnMenu() {
        this.setState({columnMenuOpen: false, columnMenuRef: null, menuColumn: undefined, menuHighlightedGrid: undefined})
    }

    popoverOnClick(sortDirection) {
        this.sortByOrder(this.state.menuColumn.field, sortDirection) 
        this.closeColumnMenu()
    }

    render() {
        return (
            <div style={{width: '100%', flex: 2, minHeight: '400px'}}>
                <DataGrid
                    // data
                    rows={this.state.rows}
                    columns={this.getDataViewColumnLayout(this.state.columns)}

                    // custom components
                    components={{
                        Toolbar: CustomToolbar,
                        Footer: CustomFooter,
                    }}

                    componentsProps={{
                        toolbar: {
                            sx: { maxHeight: '300px' }
                        },
                        footer: {
                            refreshing: this.props.refreshing,
                            timer: this.props.timer,
                            pausedAt: this.state.pausedAt,
                            toggleRefresh: this.props.toggleRefresh
                        },
                    }}

                    sx={{
                        '.MuiSvgIcon-root': { color: '#d01a62', opacity: 1 }
                    }}

                    // settings
                    rowsPerPageOptions={[50]}
                    checkboxSelection={this.props.selectable}
                    pagination
                    disableColumnMenu={true}
                    disableSelectionOnClick={true}
                    disableDensitySelector={window.screen.width < 992 ? true : false}

                    // defaults
                    pageSize={50}
                    density="compact"

                    // behavior
                    onSelectionModelChange={(selectionModel) => this.setSelectionModel(selectionModel)}
                    selectionModel={this.state.selectionModel}
                    onSortModelChange={(model) => { this.setSortingModel(model) }}
                    sortModel={this.state.sortModel}
                />
                {this.menuPopover()}
            </div>
        )
    }

    createCustomHeader(column) {
        return (
            <Box sx={{width: column.width - 32, display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <Tooltip title={column.headerName}>
                    <Typography sx={{width: column.width - 32, display: 'block', fontSize: 16}} noWrap={true}>
                        {column.headerName}
                    </Typography>
                </Tooltip>
                <MenuIcon sx={{width: 18, height: 18, color: theme.palette.primary.main, cursor: 'pointer'}} 
                    onClick={(event) => {
                        this.openColumnMenu(event, column) 
                    }}
                ></MenuIcon>
            </Box>
        )
    }

    menuPopover() {
        return (
            <Popover
                key={"menu_popover_"}
                open={this.state.columnMenuOpen}
                anchorEl={this.state.columnMenuRef}
                onClose={this.closeColumnMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                >
                <Box key={"container_popover_"} sx={{display: 'flex', flexDirection: 'column', padding: '8px', justifyContent: 'space-between', cursor: 'pointer'}}>
                    <Box key={"alert_"} sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center'}} onClick={(input) => {
                        this.props.createAlert(this.state.menuColumn) 
                        this.closeColumnMenu()
                    }}>
                        <AddAlertIcon sx={{width: 18, height: 18, color: theme.palette.primary.main}}></AddAlertIcon>
                        {this.spacer("4px", "0px")}
                        <Typography sx={{padding: '4px', fontSize: 16}}>
                            {"Create Alert"}
                        </Typography>
                    </Box>
                    <Box key={"desc_sort_"} sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center'}} 
                        onClick={(input) => this.popoverOnClick("desc") 
                    }>
                        <ArrowDownwardIcon sx={{width: 18, height: 18, color: theme.palette.primary.main}}></ArrowDownwardIcon>
                        {this.spacer("4px", "0px")}
                        <Typography sx={{padding: '4px', fontSize: 16}}>
                            {"Sort DESC"}
                        </Typography>
                    </Box>
                    <Box key={"asc_sort_"} sx={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center'}} 
                        onClick={(input) => this.popoverOnClick("asc") 
                    }>
                        <ArrowUpwardIcon sx={{width: 18, height: 18, color: theme.palette.primary.main}}></ArrowUpwardIcon>
                        {this.spacer("4px", "0px")}
                        <Typography sx={{padding: '4px', fontSize: 16}}>
                            {"Sort ASC"}
                        </Typography>
                    </Box>
                </Box>
            </Popover>
        )
    }
    
    spacer(width, height, opacity, solid) {
        return (
            <div style={{width, height, opacity, background: solid? "gray" : "transparent"}}></div>
        )
    }
}

export default MuiCustomGrid;